import DatatableIcon from '@precis-digital/kurama/icons/DatatableIcon'
import GridIcon from '@precis-digital/kurama/icons/GridIcon'
import { lighten } from '@precis-digital/kurama/utils'
import { TABLE_PAGINATION_HEIGHT } from '@precis-digital/kurama/Table'
import { type BorderedBadgeShape } from '@precis-digital/kurama/Badge'
import { type Theme, type ThemeColor } from '@precis-digital/kurama/theme'
import {
  type RechartsLabelProps as LabelProps,
  type RechartsXAxisProps as XAxisProps,
  type RechartsYAxisProps as YAxisProps
} from '@precis-digital/kurama/recharts'
import type { ForwardRefExoticComponent, ReactNode, RefAttributes } from 'react'
import type { FontProps, LegacyBadgeColor } from './internalTypes'
import type {
  ChartCardDefaults,
  ChartCardGridSize,
  ChartCardTKeys,
  ChartCardType,
  ChartType,
  DefaultDrawingInnerProps,
  DrawingColorMaps,
  LinePattern,
  Margin,
  ViewType
} from './types'

/**
 * Stable chart color names for zod validation. Color values are resolved at runtime via `useTheme()`.
 * Keys match foundation primitive hues/shades (dataViz without `*05` tints, semantic scales, primary/secondary).
 */
export const chartAvailableColorNames = [
  'primary',
  'secondary',
  'cobalt50',
  'cobalt90',
  'cobalt100',
  'cobalt110',
  'teal50',
  'teal90',
  'teal100',
  'teal110',
  'magenta50',
  'magenta90',
  'magenta100',
  'magenta110',
  'orange50',
  'orange90',
  'orange100',
  'orange110',
  'brown50',
  'brown90',
  'brown100',
  'brown110',
  'violet50',
  'violet90',
  'violet100',
  'violet110',
  'andesite50',
  'andesite90',
  'andesite100',
  'andesite110',
  'pink50',
  'pink90',
  'pink100',
  'pink110',
  'olive50',
  'olive90',
  'olive100',
  'olive110',
  'sienna50',
  'sienna90',
  'sienna100',
  'sienna110',
  'green50',
  'green90',
  'green100',
  'green110',
  'caramel50',
  'caramel90',
  'caramel100',
  'caramel110',
  'aqua50',
  'aqua90',
  'aqua100',
  'aqua110',
  'salmon50',
  'salmon90',
  'salmon100',
  'salmon110',
  'brass50',
  'brass90',
  'brass100',
  'brass110',
  'error160',
  'error150',
  'error120',
  'error110',
  'error100',
  'error90',
  'error40',
  'error05',
  'warning160',
  'warning150',
  'warning120',
  'warning110',
  'warning100',
  'warning90',
  'warning60',
  'warning05',
  'success160',
  'success150',
  'success120',
  'success110',
  'success100',
  'success90',
  'success40',
  'success05',
  'info160',
  'info150',
  'info120',
  'info110',
  'info100',
  'info90',
  'info60',
  'info05',
  'green100'
] as const

export type ChartColorName = (typeof chartAvailableColorNames)[number]

export const OthersIDSuffix = ':[others]' as const

export const TableChoicePrefix = '[table]' as const

export const TableColumnsChoice = `${TableChoicePrefix}:[columns]` as const

export const TableColumnChoicePrefix = `${TableChoicePrefix}:[column]` as const
export const TableColumnValuesChoice = `${TableColumnChoicePrefix}:values` as const

interface GraphColorTokenConfig {
  series: number
  kind: 'fill' | 'border'
  /** `tonalSoftDefault` = `fill.tonal.soft.default` (replaces removed secondary 3–8 `fill.subtle`). */
  shade: 'default' | 'subtle' | 'tonalSoftDefault'
}

const graphPrimaryColorTokens: Record<string, GraphColorTokenConfig> = {
  cobalt50: { series: 7, kind: 'fill', shade: 'subtle' },
  cobalt90: { series: 7, kind: 'border', shade: 'subtle' },
  cobalt100: { series: 7, kind: 'fill', shade: 'default' },
  cobalt110: { series: 7, kind: 'border', shade: 'default' },
  teal50: { series: 2, kind: 'fill', shade: 'subtle' },
  teal90: { series: 2, kind: 'border', shade: 'subtle' },
  teal100: { series: 2, kind: 'fill', shade: 'default' },
  teal110: { series: 2, kind: 'border', shade: 'default' },
  magenta50: { series: 4, kind: 'fill', shade: 'subtle' },
  magenta90: { series: 4, kind: 'border', shade: 'subtle' },
  magenta100: { series: 4, kind: 'fill', shade: 'default' },
  magenta110: { series: 4, kind: 'border', shade: 'default' },
  orange50: { series: 3, kind: 'fill', shade: 'subtle' },
  orange90: { series: 3, kind: 'border', shade: 'subtle' },
  orange100: { series: 3, kind: 'fill', shade: 'default' },
  orange110: { series: 3, kind: 'border', shade: 'default' },
  brown50: { series: 8, kind: 'fill', shade: 'subtle' },
  brown90: { series: 8, kind: 'border', shade: 'subtle' },
  brown100: { series: 8, kind: 'fill', shade: 'default' },
  brown110: { series: 8, kind: 'border', shade: 'default' },
  violet50: { series: 5, kind: 'fill', shade: 'subtle' },
  violet90: { series: 5, kind: 'border', shade: 'subtle' },
  violet100: { series: 5, kind: 'fill', shade: 'default' },
  violet110: { series: 5, kind: 'border', shade: 'default' },
  andesite50: { series: 6, kind: 'fill', shade: 'subtle' },
  andesite90: { series: 6, kind: 'border', shade: 'subtle' },
  andesite100: { series: 6, kind: 'fill', shade: 'default' },
  andesite110: { series: 6, kind: 'border', shade: 'default' }
}

const graphSecondaryColorTokens: Record<string, GraphColorTokenConfig> = {
  pink50: { series: 1, kind: 'fill', shade: 'subtle' },
  pink90: { series: 1, kind: 'border', shade: 'subtle' },
  pink100: { series: 1, kind: 'fill', shade: 'default' },
  pink110: { series: 1, kind: 'border', shade: 'default' },
  olive50: { series: 2, kind: 'fill', shade: 'subtle' },
  olive90: { series: 2, kind: 'border', shade: 'subtle' },
  olive100: { series: 2, kind: 'fill', shade: 'default' },
  olive110: { series: 2, kind: 'border', shade: 'default' },
  sienna50: { series: 3, kind: 'fill', shade: 'tonalSoftDefault' },
  sienna90: { series: 3, kind: 'border', shade: 'subtle' },
  sienna100: { series: 3, kind: 'fill', shade: 'default' },
  sienna110: { series: 3, kind: 'border', shade: 'default' },
  green50: { series: 4, kind: 'fill', shade: 'tonalSoftDefault' },
  green90: { series: 4, kind: 'border', shade: 'subtle' },
  green100: { series: 4, kind: 'fill', shade: 'default' },
  green110: { series: 4, kind: 'border', shade: 'default' },
  caramel50: { series: 5, kind: 'fill', shade: 'tonalSoftDefault' },
  caramel90: { series: 5, kind: 'border', shade: 'subtle' },
  caramel100: { series: 5, kind: 'fill', shade: 'default' },
  caramel110: { series: 5, kind: 'border', shade: 'default' },
  aqua50: { series: 6, kind: 'fill', shade: 'tonalSoftDefault' },
  aqua90: { series: 6, kind: 'border', shade: 'subtle' },
  aqua100: { series: 6, kind: 'fill', shade: 'default' },
  aqua110: { series: 6, kind: 'border', shade: 'default' },
  salmon50: { series: 7, kind: 'fill', shade: 'tonalSoftDefault' },
  salmon90: { series: 7, kind: 'border', shade: 'subtle' },
  salmon100: { series: 7, kind: 'fill', shade: 'default' },
  salmon110: { series: 7, kind: 'border', shade: 'default' },
  brass50: { series: 8, kind: 'fill', shade: 'tonalSoftDefault' },
  brass90: { series: 8, kind: 'border', shade: 'subtle' },
  brass100: { series: 8, kind: 'fill', shade: 'default' },
  brass110: { series: 8, kind: 'border', shade: 'default' }
}

const semanticColorTokens: Record<string, keyof Theme['tokens']['background']['semantic'] | 'green100'> = {
  error160: 'error',
  error150: 'error',
  error120: 'error',
  error110: 'error',
  error100: 'error',
  error90: 'error',
  error40: 'error',
  error05: 'error',
  warning160: 'warning',
  warning150: 'warning',
  warning120: 'warning',
  warning110: 'warning',
  warning100: 'warning',
  warning90: 'warning',
  warning60: 'warning',
  warning05: 'warning',
  success160: 'success',
  success150: 'success',
  success120: 'success',
  success110: 'success',
  success100: 'success',
  success90: 'success',
  success40: 'success',
  success05: 'success',
  info160: 'info',
  info150: 'info',
  info120: 'info',
  info110: 'info',
  info100: 'info',
  info90: 'info',
  info60: 'info',
  info05: 'info',
  green100: 'success'
}

const resolveGraphColor = (theme: Theme, family: 'primary' | 'secondary', key: string): ThemeColor | undefined => {
  const config = family === 'primary' ? graphPrimaryColorTokens[key] : graphSecondaryColorTokens[key]
  if (config == null) {
    return undefined
  }

  const series = theme.tokens.dataViz[family][config.series as keyof (typeof theme.tokens.dataViz)['primary']]

  if (config.kind === 'fill' && config.shade === 'tonalSoftDefault') {
    return series.fill.tonal.soft.default
  }

  if (config.kind === 'fill') {
    if (config.shade === 'default') {
      return series.fill.default
    }
    if (config.shade === 'subtle' && 'subtle' in series.fill) {
      return series.fill.subtle
    }
    return undefined
  }

  if (config.shade === 'default') {
    return series.border.default
  }
  if (config.shade === 'subtle') {
    return series.border.subtle
  }
  return undefined
}

const resolveSemanticChartColor = (theme: Theme, key: string): ThemeColor | undefined => {
  const semanticKey = semanticColorTokens[key]
  if (semanticKey == null || semanticKey === 'green100') {
    if (key === 'green100') {
      return theme.tokens.dataViz.secondary[4].fill.default
    }
    return undefined
  }

  const shade = /\d+/.exec(key)?.[0]
  const semantic = theme.tokens.background.semantic[semanticKey].main
  if (shade === '05') {
    return theme.tokens.background.semantic[semanticKey].tonal.soft.default
  }
  if (shade === '40' || shade === '60') {
    return theme.tokens.background.semantic[semanticKey].tonal.medium.default
  }
  if (shade != null && Number(shade) >= 110) {
    return semantic.pressed
  }
  if (shade === '90') {
    return semantic.hover
  }
  return semantic.default
}

export const createChartAvailableColorNamesMap = (theme: Theme): Record<ChartColorName, ThemeColor> => {
  const result: Partial<Record<ChartColorName, ThemeColor>> = {
    primary: theme.tokens.background.brand.main.default,
    secondary: theme.tokens.foreground.main.primary
  }

  for (const key of Object.keys(graphPrimaryColorTokens)) {
    const color = resolveGraphColor(theme, 'primary', key)
    if (color != null) {
      result[key as ChartColorName] = color
    }
  }

  for (const key of Object.keys(graphSecondaryColorTokens)) {
    const color = resolveGraphColor(theme, 'secondary', key)
    if (color != null) {
      result[key as ChartColorName] = color
    }
  }

  for (const key of Object.keys(semanticColorTokens)) {
    const color = resolveSemanticChartColor(theme, key)
    if (color != null) {
      result[key as ChartColorName] = color
    }
  }

  return result as Record<ChartColorName, ThemeColor>
}

export const createChartAvailableColorNames = (
  chartAvailableColorNamesMap: Record<ChartColorName, ThemeColor>
): readonly ChartColorName[] => Object.keys(chartAvailableColorNamesMap) as readonly ChartColorName[]

export const createChartAvailableColors = (
  chartAvailableColorNamesMap: Record<ChartColorName, ThemeColor>
): readonly ThemeColor[] => Object.values(chartAvailableColorNamesMap)

/**
 * Distinct series colours for auto-assignment when metrics/dimension values omit a colour.
 * Prefers `*50` fill shades so consecutive series get different hues (not cobalt50/90/100…).
 */
export const createChartSeriesColors = (
  chartAvailableColorNamesMap: Record<ChartColorName, ThemeColor>
): readonly ThemeColor[] =>
  (Object.keys(chartAvailableColorNamesMap) as ChartColorName[])
    .filter((name) => name.endsWith('50'))
    .map((name) => chartAvailableColorNamesMap[name])

export const chartHasAxis: Record<ChartType, boolean> = {
  scatter: true,
  bar: true,
  line: true,
  gradientLine: true,
  area: true,
  pie: false,
  donut: false
} as const satisfies Record<ChartType, boolean>

export const chartHasTooltip: Record<ChartType, boolean> = {
  scatter: true,
  bar: true,
  line: true,
  gradientLine: true,
  area: true,
  pie: false,
  donut: false
} as const satisfies Record<ChartType, boolean>

export const chartAllowsZooming: Record<ChartType, boolean> = {
  scatter: true,
  bar: true,
  line: true,
  gradientLine: true,
  area: true,
  pie: false,
  donut: false
} as const satisfies Record<ChartType, boolean>

export const chartMetricShowLegendBadge: Record<ChartType, boolean> = {
  area: true,
  bar: true,
  line: true,
  gradientLine: true,
  scatter: false,
  pie: false,
  donut: false
} as const satisfies Record<ChartType, boolean>

export const getDrawingColorMaps = (): DrawingColorMaps => ({
  primary: {
    area: 'stroke',
    bar: 'fill',
    line: 'stroke',
    gradientLine: 'stroke',
    scatter: 'stroke',
    pie: 'fill',
    donut: 'fill'
  },
  secondary: {
    area: 'fill',
    bar: 'stroke',
    line: undefined,
    gradientLine: 'fill',
    scatter: 'stroke',
    pie: 'stroke',
    donut: 'stroke'
  }
})

export const chartTypeToView: Record<ChartType, ViewType> = {
  scatter: 'cartesian',
  bar: 'cartesian',
  line: 'cartesian',
  gradientLine: 'cartesian',
  area: 'cartesian',
  pie: 'polar',
  donut: 'polar'
} as const satisfies Record<ChartType, ViewType>

export const CardMargin = 19

export const CHART_CARD_CHART_MARGIN = {
  top: 8,
  right: 8,
  left: 8,
  bottom: 8
} as const

export const CHART_CARD_TABLE_MARGIN = {
  top: 0,
  right: 0,
  left: 0,
  bottom: 0
} as const

export const CHART_CARD_CHART_VIEW_MARGIN: Partial<Record<ViewType, Margin>> = {
  polar: {
    top: 16,
    right: 16,
    left: 0,
    bottom: 16
  }
}

export const CHART_CARD_CARD_LAYOUT = {
  padding: 19,
  gap: 24,
  cardsFixedColumns: false
} as const

export const CHART_CARD_PERFORMANCE_MIN_WIDTH = 192

/** Chart minimum plus one card gap on each side — the scorecard column floor. */
export const CHART_CARD_PERFORMANCE_COLUMN_MIN_WIDTH = CHART_CARD_PERFORMANCE_MIN_WIDTH + CHART_CARD_CARD_LAYOUT.gap * 2

export const CHART_CARD_TABLE_LAYOUT = {
  height: {
    title: 40,
    pagination: TABLE_PAGINATION_HEIGHT
  },
  defaultColumn: {
    minSize: 48,
    maxSize: 1088
  },
  resizeIconSize: 24,
  padding: {
    head: { top: 2, right: 12, left: 8, bottom: 4 },
    body: { top: 4, right: 12, left: 8, bottom: 4 },
    footer: { top: 8, right: 12, left: 8, bottom: 8 }
  }
} as const

export const ZOOM_OVERLAY_MIN_CHANGE_FACTOR = 0.05

export const OTHERS_NON_ACTIVE_FILL_OPACITY = 0.3

export const TOOLTIP_ANIMATION_DURATION = 200

export const TOOLTIP_OUT_OF_ZOOM_OPACITY = 0.5

export const CHART_CARD_AXIS_STATIC = {
  angledLabels: {
    fallbackLength: 20,
    fallbackSpace: 100,
    angle: -50,
    wrapLines: 1,
    maxSpace: 150
  },
  space: {
    verticalLabelOffset: 40,
    horizontalLabelOffset: 24,
    xDY: 5,
    xStandardHorizontal: 20,
    xStandardVertical: 50,
    xScatter: 50,
    yHorizontal: 70,
    yVertical: 200
  }
} as const

export const CHART_CARD_EXPORT_LAYOUT = {
  labelGap: 4,
  itemsGap: 8,
  groupedItemsGap: 4,
  headerBottomPadding: 16,
  fallbackFontFamilySVG: 'Roboto',
  fallbackFontFamilyPNG: 'Arial,sans-serif',
  scalePNG: 2
} as const

export const sizeToMinCardHeight: Record<ChartCardGridSize, number> = {
  small: 238,
  medium: 338,
  large: 438,
  full: 538
} as const satisfies Record<ChartCardGridSize, number>

export const sizeToMinCardWidth: Record<ChartCardGridSize, number> = {
  small: 282,
  medium: 382,
  large: 482,
  full: 632
} as const satisfies Record<ChartCardGridSize, number>

export const sizeToMinChartHeight: Record<ChartCardGridSize, number> = {
  small: sizeToMinCardHeight.small - 19 * 2,
  medium: sizeToMinCardHeight.medium - 19 * 2,
  large: sizeToMinCardHeight.large - 19 * 2,
  full: sizeToMinCardHeight.full - 19 * 2
} as const satisfies Record<ChartCardGridSize, number>

export const sizeToMinChartWidth: Record<ChartCardGridSize, number> = {
  small: sizeToMinCardWidth.small - 19 * 2,
  medium: sizeToMinCardWidth.medium - 19 * 2,
  large: sizeToMinCardWidth.large - 19 * 2,
  full: sizeToMinCardWidth.full - 19 * 2
} as const satisfies Record<ChartCardGridSize, number>

export const sizeToTickCount: Record<ChartCardGridSize, number> = {
  small: 4,
  medium: 8,
  large: 10,
  full: 10
} as const satisfies Record<ChartCardGridSize, number>

export const tickCountBreakpoints = {
  height: {
    0: 1,
    200: 4,
    300: 8,
    400: 10,
    500: 10
  },
  width: {
    0: 1,
    244: 5,
    344: 7,
    444: 9,
    644: 15
  }
} as const satisfies Record<'height' | 'width', Record<number, number>>

export const linePatterns: Record<LinePattern, string | undefined> = {
  continuous: undefined,
  dot: '1 5',
  dash: '4 5',
  line: '7 5',
  'dash-dot': '4 5 1 5',
  'line-dot': '7 5 1 5'
} as const satisfies Record<LinePattern, string | undefined>

export const LinePatterns: LinePattern[] = ['continuous', 'dot', 'dash', 'line', 'line-dot', 'dash-dot']

export const createLegacyBadgeColorMap = (theme: Theme): Record<LegacyBadgeColor, ThemeColor> =>
  ({
    pink: theme.tokens.dataViz.secondary[1].fill.subtle,
    steel: theme.tokens.foreground.main.secondary,
    mint: theme.tokens.dataViz.secondary[4].fill.tonal.soft.default,
    'dark-mint': theme.tokens.dataViz.primary[2].border.subtle,
    beige: theme.tokens.dataViz.secondary[5].fill.tonal.soft.default,
    'dark-beige': theme.tokens.dataViz.secondary[5].fill.tonal.soft.default,
    indigo: theme.tokens.dataViz.primary[1].border.halo,
    'dark-indigo': theme.tokens.dataViz.primary[5].border.subtle,
    yellow: theme.tokens.dataViz.secondary[8].fill.tonal.soft.default,
    red: theme.tokens.dataViz.secondary[7].fill.tonal.soft.default,
    'dark-red': theme.tokens.dataViz.primary[3].fill.subtle,
    green: theme.tokens.dataViz.secondary[4].fill.default,
    purple: theme.tokens.dataViz.primary[5].fill.default,
    'dark-purple': theme.tokens.dataViz.primary[6].fill.default,
    'extra-dark-purple': theme.tokens.dataViz.primary[6].border.default,
    success: theme.tokens.background.semantic.success.main.default,
    error: theme.tokens.background.semantic.error.main.default,
    warning: theme.tokens.background.semantic.warning.main.default,
    info: theme.tokens.background.semantic.info.main.default,
    stone: theme.tokens.background.tonal.soft.default
  } as const satisfies Record<LegacyBadgeColor, ThemeColor>)

export const linePatternToBadgeShape: Record<LinePattern, BorderedBadgeShape> = {
  continuous: 'lineSolid',
  line: 'lineSolid',
  dot: 'lineDotted',
  dash: 'lineDashed',
  'line-dot': 'lineDashed',
  'dash-dot': 'lineDashed'
} as const satisfies Record<LinePattern, BorderedBadgeShape>

export const isLineChartType = (chartType: ChartType | undefined): boolean =>
  chartType === 'line' || chartType === 'gradientLine'

export const SelectedOthers = '+'
export const NotSelectedOthers = '-'

/** Placeholder rendered wherever a null dimension value would otherwise be shown as a label. */
export const NULL_DIMENSION_VALUE_DISPLAY = '-'

/**
 * Canonical data / option / selection value for a missing dimension, rendered as
 * {@link NULL_DIMENSION_VALUE_DISPLAY}.
 *
 * A namespaced token rather than an empty string or {@link NULL_DIMENSION_VALUE_DISPLAY}:
 * empty strings are normalised back to null across the pipeline, a hyphen collides with
 * {@link NotSelectedOthers} and with real dimension values, and Recharts discards falsy
 * categories (dropping them from a category axis domain and naming polar slices after the
 * metric key instead).
 */
export const NULL_DIMENSION_VALUE_OPTION = '__null_value__'

const createDefaultChartTitleFontProps = (theme: Theme): FontProps =>
  ({
    fontFamily: theme.typography.h3.fontFamily,
    fontWeight: theme.typography.h3.fontWeight,
    fontSize: theme.typography.h3.fontSize,
    letterSpacing: theme.typography.h3.letterSpacing,
    fill: theme.tokens.foreground.main.primary
  } as const satisfies LabelProps)

const createDefaultAxisLabelProps = (theme: Theme): LabelProps =>
  ({
    fontFamily: theme.typography.h5.fontFamily,
    fontWeight: theme.typography.h5.fontWeight,
    fontSize: theme.typography.h5.fontSize,
    letterSpacing: theme.typography.h5.letterSpacing,
    fill: theme.tokens.foreground.main.primary
  } as const satisfies FontProps)

const createDefaultAxisTicksFontProps = (theme: Theme): FontProps =>
  ({
    fontFamily: theme.typography.body2.fontFamily,
    fontWeight: theme.typography.body2.fontWeight,
    fontSize: theme.typography.body2.fontSize,
    letterSpacing: theme.typography.body2.letterSpacing,
    fill: theme.tokens.foreground.main.primary
  } as const satisfies LabelProps)

const createDefaultExportLegendChipsFontProps = (theme: Theme): FontProps =>
  ({
    fontFamily: theme.typography.body3.fontFamily,
    fontWeight: theme.typography.body3.fontWeight,
    fontSize: theme.typography.body3.fontSize,
    letterSpacing: theme.typography.body3.letterSpacing,
    fill: theme.tokens.foreground.main.primary
  } as const satisfies FontProps)

const createDefaultXAxisLineProps = (theme: Theme): XAxisProps['axisLine'] =>
  ({
    stroke: theme.tokens.foreground.main.disabled,
    strokeWidth: 1,
    strokeOpacity: 1
  } as const satisfies XAxisProps['axisLine'])

const createDefaultYAxisLineProps = (theme: Theme): YAxisProps['axisLine'] =>
  ({
    stroke: theme.tokens.foreground.main.disabled,
    strokeWidth: 1,
    strokeOpacity: 1
  } as const satisfies YAxisProps['axisLine'])

const createDefaultPieProps = (theme: Theme): DefaultDrawingInnerProps['pie'] =>
  ({
    startAngle: 90,
    endAngle: 90 - 360,
    stroke: theme.tokens.background.main.default,
    strokeWidth: 2,
    strokeOpacity: 1,
    outerRadius: '100%',
    cx: '50%',
    cy: '50%',
    paddingAngle: 0,
    cornerRadius: 8,
    highlightProps: {
      outerRadius: '113%',
      fillShadeOffset: 0.7
    },
    inactiveProps: {
      fillShadeOffset: 0.65
    }
  } as const satisfies DefaultDrawingInnerProps['pie'])

export const createDefaultDrawingProps = (
  theme: Theme,
  chartAvailableColors: readonly ThemeColor[]
): DefaultDrawingInnerProps => {
  const defaultPieProps = createDefaultPieProps(theme)

  return {
    area: {
      scale: 'auto',
      type: 'monotoneX',
      strokeWidth: 4,
      stroke: chartAvailableColors[0],
      strokeOpacity: 1,
      fill: lighten(chartAvailableColors[0], 0.3),
      fillOpacity: 1,
      stackId: '1',
      connectNulls: true,
      animationDuration: 200
    },
    bar: {
      scale: 'auto',
      strokeWidth: 4,
      stroke: chartAvailableColors[0],
      strokeOpacity: 0,
      fill: chartAvailableColors[0],
      fillOpacity: 1,
      radius: [9999, 9999, 0, 0],
      animationDuration: 200
    },
    line: {
      scale: 'auto',
      type: 'monotone',
      stroke: chartAvailableColors[0],
      strokeWidth: 4,
      strokeLinecap: 'round',
      strokeDasharray: linePatterns.continuous,
      strokeOpacity: 1,
      dot: false,
      connectNulls: true,
      animationDuration: 100
    },
    gradientLine: {
      scale: 'auto',
      type: 'monotoneX',
      strokeWidth: 4,
      strokeLinecap: 'round',
      stroke: chartAvailableColors[0],
      strokeOpacity: 1,
      fill: lighten(chartAvailableColors[0], 0.3),
      fillOpacity: 1,
      stackId: '1',
      dot: false,
      connectNulls: true,
      animationDuration: 100
    },
    pie: defaultPieProps,
    donut: {
      ...defaultPieProps,
      innerRadius: '50%'
    },
    scatter: {
      shape: {
        stroke: 'none',
        fill: chartAvailableColors[0],
        fillOpacity: 1
      },
      animationDuration: 200
    }
  } as const satisfies DefaultDrawingInnerProps
}

const tableHeaderDefaultColumn = (): ReactNode => null
const tableCellDefaultColumn = (): ReactNode => null

export const createDefaults = (theme: Theme, chartAvailableColors: readonly ThemeColor[]): ChartCardDefaults => {
  const defaultDrawingProps = createDefaultDrawingProps(theme, chartAvailableColors)

  return {
    card: {
      padding: 19,
      gap: 24,
      cardsFixedColumns: false
    },
    chartMargin: {
      top: 8,
      right: 8,
      left: 8,
      bottom: 8
    },
    tableMargin: {
      top: 0,
      right: 0,
      left: 0,
      bottom: 0
    },
    chartViewMargin: {
      polar: {
        top: 16,
        right: 16,
        left: 0,
        bottom: 16
      }
    },
    cardChartSizes: {
      performance: {
        height: 72,
        minWidth: CHART_CARD_PERFORMANCE_MIN_WIDTH
      }
    },
    axis: {
      angledLabels: {
        fallbackLength: 20,
        fallbackSpace: 100,
        angle: -50,
        wrapLines: 1,
        maxSpace: 150
      },
      space: {
        verticalLabelOffset: 40,
        horizontalLabelOffset: 24,
        xDY: 5,
        xStandardHorizontal: 20,
        xStandardVertical: 50,
        xScatter: 50,
        yHorizontal: 70,
        yVertical: 200
      },
      xLine: createDefaultXAxisLineProps(theme),
      yLine: createDefaultYAxisLineProps(theme),
      format: {
        money: {
          currencyCode: 'EUR',
          compact: true,
          showCurrency: true,
          maximumFractionDigits: 1,
          minimumFractionDigits: 0
        },
        number: {
          compact: true,
          maximumFractionDigits: 1,
          minimumFractionDigits: 0
        },
        percentage: {
          maximumFractionDigits: 1,
          minimumFractionDigits: 0
        }
      }
    },
    table: {
      height: {
        title: 40,
        pagination: TABLE_PAGINATION_HEIGHT
      },
      padding: {
        head: {
          top: 2,
          right: 12,
          left: 8,
          bottom: 4
        },
        body: {
          top: 4,
          right: 12,
          left: 8,
          bottom: 4
        },
        footer: {
          top: 8,
          right: 12,
          left: 8,
          bottom: 8
        }
      },
      format: {
        money: {
          currencyCode: 'EUR',
          compact: true,
          showCurrency: true,
          maximumFractionDigits: 2,
          minimumFractionDigits: 0
        },
        number: {
          compact: true,
          maximumFractionDigits: 2,
          minimumFractionDigits: 0
        },
        percentage: {
          maximumFractionDigits: 1,
          minimumFractionDigits: 0
        }
      },
      defaultColumn: {
        minSize: 48,
        maxSize: 1088,
        header: tableHeaderDefaultColumn,
        cell: tableCellDefaultColumn
      },
      hoverBackgroundColor: {
        body: theme.tokens.background.main.pressed
      },
      backgroundColor: {
        head: theme.tokens.background.main.pressed,
        footer: theme.tokens.background.main.default
      },
      resizeIconSize: 24,
      allowResizingShadow: {},
      endPinnedShadow: {
        body: {
          left: `-1px 0 0 0 ${theme.tokens.background.tonal.soft.default} inset`,
          right: `1px 0 0 0 ${theme.tokens.background.tonal.soft.default} inset`
        },
        footer: {
          left: `-1px 0 0 0 ${theme.tokens.background.tonal.soft.default} inset`,
          right: `1px 0 0 0 ${theme.tokens.background.tonal.soft.default} inset`
        }
      },
      shadows: {
        head: `0 1px 0 0 ${theme.tokens.background.tonal.soft.default}`,
        body: `0 1px 0 0 ${theme.tokens.background.tonal.soft.default} inset`,
        footer: `0 1px 0 0 ${theme.tokens.background.tonal.soft.default} inset`
      },
      colorScale: {
        min: 0.1,
        max: 0.8
      }
    },
    fonts: {
      title: createDefaultChartTitleFontProps(theme),
      axisLabels: createDefaultAxisLabelProps(theme),
      axisTicks: createDefaultAxisTicksFontProps(theme),
      exportLegendLabels: createDefaultExportLegendChipsFontProps(theme),
      exportLegendChips: createDefaultExportLegendChipsFontProps(theme)
    },
    chart: {
      area: {},
      bar: {
        barGap: '6%',
        barCategoryGap: '20%'
      },
      line: {},
      gradientLine: {},
      composed: {},
      pie: {},
      donut: {},
      scatter: {}
    },
    drawing: defaultDrawingProps,
    specialDrawing: {
      gradientLine: {
        stops: [
          { offset: '5%', stopOpacity: 0.3 },
          { offset: '95%', stopOpacity: 0 }
        ]
      }
    },
    cardDrawing: {
      performance: {
        line: { activeDot: false },
        area: { activeDot: false },
        gradientLine: { activeDot: false }
      }
    },
    drawingColorMaps: getDrawingColorMaps(),
    zoomOverlay: {
      stroke: theme.tokens.background.brand.main.default,
      strokeWidth: 4,
      strokeOpacity: 1,
      fillOpacity: 0.3,
      fill: theme.tokens.background.brand.main.default,
      minChangeFactor: 0.05
    },
    tooltip: {
      format: {
        money: {
          currencyCode: 'EUR',
          compact: false,
          showCurrency: true,
          maximumFractionDigits: 1,
          minimumFractionDigits: 0
        },
        number: {
          compact: false,
          maximumFractionDigits: 1,
          minimumFractionDigits: 0
        },
        percentage: {
          maximumFractionDigits: 1,
          minimumFractionDigits: 0
        }
      },
      outOfZoomOpacity: 0.5
    },
    others: {
      nonActiveFillOpacity: 0.3,
      tooltipAnimationDuration: 200
    },
    export: {
      labelGap: 4,
      itemsGap: 8,
      groupedItemsGap: 4,
      headerBottomPadding: 16,
      fallbackFontFamilySVG: 'Roboto',
      fallbackFontFamilyPNG: 'Arial,sans-serif',
      scalePNG: 2
    }
  } as const satisfies ChartCardDefaults
}

export const defaultTranslations: Record<ChartCardTKeys, string> = {
  collapse: 'Collapse',
  expand: 'Expand',
  resetChart: 'Reset chart',
  resetZooming: 'Reset zooming',
  others: 'Others',
  othersTitle: 'Other {{labels}}',
  select: 'Select',
  selectOthers: 'Group others',
  selectOthersTitle:
    'Select this to group all unselected {{labels}} in the chart into a single aggregated {{label}} named "Others".',
  exportAs: 'Export as …',
  exportAsCSV: 'Export as CSV',
  exportAsGoogleSheets: 'Export to Google Sheets',
  exportAsSVG: 'Export as SVG',
  copyAsSVG: 'Copy as SVG',
  exportAsPNG: 'Export as PNG',
  copyAsPNG: 'Copy as PNG',
  svgCopiedToClipboard: 'SVG copied to clipboard',
  pngCopiedToClipboard: 'PNG copied to clipboard',
  nothingToExport: 'Nothing to export from this table',
  failedToExportGoogleSheets: 'Failed to export to Google Sheets',
  failedToGenerateSVG: 'Failed to generate SVG',
  failedToGeneratePNG: 'Failed to generate PNG',
  failedToCopySVG: 'Failed to copy SVG',
  failedToCopyPNG: 'Failed to copy PNG',
  sortAscending: 'Sort ascending',
  sortDescending: 'Sort descending',
  sortNone: 'Do not sort by this column',
  filterData: 'Filter data',
  filteredData: 'Filtered data',
  noDataFiltering: 'No data filtering available for this column',
  pinColumn: 'Pin column',
  noPinColumn: 'Column cannot be pinned',
  noUnpinColumn: 'Column cannot be unpinned',
  pinLeft: 'Pin left',
  pinRight: 'Pin right',
  noPin: 'No pin',
  autosizeColumn: 'Autosize this column',
  autosizeAllColumns: 'Autosize all columns',
  colorScale: 'Color scale',
  selectColumns: 'Select columns',
  columns: 'Columns',
  changeToCardType: 'Change to {{cardType}}',
  noDataTitle: 'Oh no!',
  noDataBody: "Looks like we couldn't find any results for your selection. Check your filters.",
  noMetricsTitle: 'No metrics have been selected.',
  noMetricsBody: 'Select metrics to get started.',
  noDimensionsTitle: 'No dimensions have been selected.',
  noDimensionsBody: 'Select dimensions to get started.',
  dataErrorTitle: 'Oops! This chart needs a little tune-up.',
  dataErrorBody: 'There was an error fetching the data.\nPlease try again.',
  pagination: 'Pagination',
  selectAllRows: 'Select all',
  selectNoneRows: 'Select none',
  resetTable: 'Reset table',
  cannotRemoveLastDimension: 'At least one dimension should be selected',
  noData: 'No data'
}

export const CHART_CARD_TYPE_TO_NUMBER: Record<ChartCardType, number> = {
  table: 0,
  chart: 1,
  performance: 2
} as const satisfies Record<ChartCardType, number>

export const NUMBER_TO_CHART_CARD_TYPE: Record<number, ChartCardType> = {
  0: 'table',
  1: 'chart'
} as const satisfies Record<number, ChartCardType>

export const CARD_TYPE_TO_ICON: Record<ChartCardType, ForwardRefExoticComponent<RefAttributes<SVGSVGElement>>> = {
  table: DatatableIcon,
  chart: GridIcon,
  performance: GridIcon
}

// Formula validation limits to prevent DoS attacks
export const FORMULA_MAX_LENGTH = 500
export const FORMULA_MAX_OPERATOR_COUNT = 50
export const FORMULA_MAX_NESTING_DEPTH = 10
